/**
 * useCopilotChat — ONE conversation engine for every Merlin surface on the
 * Quality door (Phase 2, kanban 12584b08).
 *
 * The Ask Merlin tab and the split workspace's conversation pane run the
 * same machinery: load a saved thread, send a typed or spoken turn (with any
 * attached files uploaded first), fold the SSE frames into turns, play the
 * spoken rundown the moment it streams in, Stop an in-flight turn, and keep
 * the voice/model settings. Extracted from QualityCopilotPage so the
 * workspace never grows a second, feature-poor composer (the retired triage
 * board had one: no mic, no attachments, no Stop, no rundown).
 *
 * Deliberately NOT here: the history drawer, the "+ New" reset policy, and
 * the ?conversation= resume — those are the Ask tab's own concerns.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTextToSpeech } from '../../copilot/hooks/useTextToSpeech';
import { transcribeAudio } from '../../copilot/services/copilotApi';
import type { AudioRecordingResult } from '../../hooks/useAudioRecording';
import {
  useComposerAttachments,
  type ComposerAttachments,
} from '../chat/useComposerAttachments';
import {
  cancelMessage,
  createConversation,
  getConversationDetail,
  getModels,
  streamCopilotMessage,
  uploadAttachment,
  type ModelOption,
} from '../../services/qualityCopilot';
import { makeRundownPlayer } from '../../pages/qualityCopilotPlayback';
import {
  conversationTotals,
  detailToCopilotTurns,
  reduceCopilotEvent,
  type CopilotTurn,
} from '../../pages/qualityCopilotTurns';
import {
  readActiveConversationId,
  writeActiveConversationId,
} from '../../pages/qualityCopilotHistory';
import {
  AUTOPLAY_DEFAULT,
  readAutoplayPref,
  writeAutoplayPref,
} from '../../pages/qualityCopilotPrefs';

// Google Cloud TTS voices the Quality Co-Pilot offers (mirrors the curated
// allowlist in services/voice/gcp_tts.py). Chirp 3 HD is Google's newest tier.
export const GCP_VOICES: ReadonlyArray<{ name: string; label: string }> = [
  { name: 'en-US-Chirp3-HD-Aoede', label: 'Aoede — female (HD)' },
  { name: 'en-US-Chirp3-HD-Charon', label: 'Charon — male (HD)' },
  { name: 'en-US-Chirp3-HD-Despina', label: 'Despina — female (HD)' },
  { name: 'en-US-Chirp3-HD-Achird', label: 'Achird — male (HD)' },
  { name: 'en-US-Chirp3-HD-Callirrhoe', label: 'Callirrhoe — female (HD)' },
  { name: 'en-US-Neural2-F', label: 'Neural2 — female' },
  { name: 'en-US-Studio-O', label: 'Studio — female (narration)' },
];
export const DEFAULT_VOICE = 'en-US-Chirp3-HD-Aoede';

export interface UseCopilotChatOptions {
  /** The logged-in user — scopes the remembered thread + autoplay pref. */
  userId: string | null;
  /**
   * Remember the active conversation per user so the Ask tab resumes it.
   * The workspace pane leaves this OFF: an investigator thread is the
   * thread's, not the reader's last chat.
   */
  rememberActive?: boolean;
  /**
   * Create a conversation on the first send when none is loaded (the Ask
   * tab). The workspace pane sets this false — it only ever continues the
   * thread's existing conversation.
   */
  createOnDemand?: boolean;
  /** A send just created a brand-new thread server-side (refresh history). */
  onConversationCreated?: () => void;
}

export interface CopilotChat {
  turns: CopilotTurn[];
  setTurns: React.Dispatch<React.SetStateAction<CopilotTurn[]>>;
  patchTurn: (id: string, patch: (t: CopilotTurn) => Partial<CopilotTurn>) => void;
  /** Fresh turn ids for callers that append their own turns (the form path). */
  nextId: () => string;
  busy: boolean;
  cancelling: boolean;
  transcribing: boolean;
  loadingConvo: boolean;
  /** The last loadConversation() could not read the conversation (removed,
   *  or it belongs to another account). Cleared by the next load/reset. */
  loadFailed: boolean;
  error: string | null;
  setError: (message: string | null) => void;
  /** The conversation on screen (null before the first send / after reset). */
  activeId: string | null;
  /** Point at a saved thread and render its transcript. */
  loadConversation: (id: string) => Promise<void>;
  /** Forget the loaded thread: empty transcript, next send starts fresh. */
  resetConversation: () => void;
  /** Return the loaded conversation id, creating one when allowed. */
  ensureConversation: () => Promise<{ id: string; createdNew: boolean }>;
  /** Run an exclusive unit of work behind the composer's busy state. */
  withBusy: (work: () => Promise<void>) => Promise<void>;
  send: (raw: string, viaVoice: boolean) => Promise<void>;
  handleAudio: (result: AudioRecordingResult) => Promise<void>;
  handleStop: () => Promise<void>;
  textValue: string;
  setTextValue: (value: string) => void;
  submitTyped: () => void;
  /** Send a given text as the user's next turn (a one-click request). */
  sendText: (text: string) => void;
  attach: ComposerAttachments;
  /** Rundown playback. */
  replay: (turnId: string, spoken: string) => void;
  stopSpeaking: () => void;
  speakingId: string | null;
  isSpeaking: boolean;
  isSynthesizing: boolean;
  autoplay: boolean;
  setAutoplay: (next: boolean) => void;
  voice: string;
  setVoice: (voice: string) => void;
  speed: number;
  setSpeed: (speed: number) => void;
  models: ModelOption[];
  defaultModel: string;
  selectedModelId: string;
  setSelectedModelId: (id: string) => void;
  strongestModel: string;
  /** Running token/cost total across the conversation (admin badge). */
  totals: ReturnType<typeof conversationTotals>;
}

export function useCopilotChat({
  userId,
  rememberActive = false,
  createOnDemand = true,
  onConversationCreated,
}: UseCopilotChatOptions): CopilotChat {
  const [turns, setTurns] = useState<CopilotTurn[]>([]);
  const [busy, setBusy] = useState(false);
  // True between a Stop click and the server's terminal cancelled/done frames
  // (the Stop button shows "Stopping…" and won't double-fire).
  const [cancelling, setCancelling] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [loadingConvo, setLoadingConvo] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Auto-play of the spoken rundown defaults to OFF — a fresh/unauthenticated
  // session stays silent unless the user opts in. A saved per-user preference
  // is restored once the logged-in user is known (see the hydration effect).
  const [autoplay, setAutoplayState] = useState(AUTOPLAY_DEFAULT);
  const [voice, setVoice] = useState<string>(DEFAULT_VOICE);
  const [speed, setSpeed] = useState(1.3);
  const [textValue, setTextValue] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  // Files attached to the NEXT voice/text turn — photos or documents, staged
  // by drag-drop / paste / the 📎 picker (shared composer-attachments hook).
  // Uploaded first (so the backend has the gs_uri before the turn runs), then
  // the message is sent; Merlin can Read them and the post-turn binder ties
  // photos to the signal(s).
  const attach = useComposerAttachments({ disabled: busy });
  // Selectable models + this door's default (loaded once). The picker
  // preselects the active thread's model on resume, the door default fresh.
  const [models, setModels] = useState<ModelOption[]>([]);
  const [defaultModel, setDefaultModel] = useState<string>('');
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  // The strongest selectable model — drives the "try a stronger model" nudge.
  const [strongestModel, setStrongestModel] = useState<string>('');
  // The turn whose spoken rundown is currently playing — drives the live
  // "speaking" animation on that turn's rundown bar. Cleared when it ends.
  const [speakingId, setSpeakingId] = useState<string | null>(null);

  const totals = useMemo(() => conversationTotals(turns), [turns]);

  // Mirrors for the async send() closure (which can't read fresh state).
  const conversationIdRef = useRef<string | null>(null);
  const busyRef = useRef(false);
  // The id of the in-flight turn (from its ``started`` frame) so Stop targets
  // exactly this run — a stale click can never abort a newer turn.
  const runIdRef = useRef<string | null>(null);
  const autoplayRef = useRef(AUTOPLAY_DEFAULT);
  autoplayRef.current = autoplay;
  const selectedModelRef = useRef('');
  selectedModelRef.current = selectedModelId;
  const pendingFilesRef = useRef<File[]>([]);
  pendingFilesRef.current = attach.files;
  const idCounter = useRef(0);
  const onCreatedRef = useRef(onConversationCreated);
  onCreatedRef.current = onConversationCreated;

  // The Quality Co-Pilot speaks via Google Cloud TTS (Chirp 3 HD by default —
  // it runs on the app's service account, so it works on staging too).
  // `speed` drives the settings slider and live-rates playback.
  const tts = useTextToSpeech({
    voice,
    rate: speed,
    provider: 'google',
    onComplete: () => setSpeakingId(null),
  });

  const nextId = useCallback(() => `t${++idCounter.current}`, []);

  // Load the selectable models once (non-fatal): the picker just won't render.
  useEffect(() => {
    let cancelled = false;
    getModels()
      .then((data) => {
        if (cancelled) return;
        setModels(data.models);
        setDefaultModel(data.default_model);
        setStrongestModel(data.strongest_model);
        setSelectedModelId((prev) => prev || data.default_model);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Unlock audio autoplay on the user's FIRST interaction with the page, so a
  // (delayed) spoken rundown auto-plays instead of being blocked by the
  // browser autoplay policy ("it doesn't always start; I pause then play").
  const primeAudio = tts.prime;
  useEffect(() => {
    const unlock = () => {
      primeAudio();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [primeAudio]);

  // Restore the user's saved Auto-play preference (default OFF). Re-reads when
  // the logged-in user changes so a shared browser never leaks one staffer's
  // setting onto another; before auth resolves it reads the anon bucket,
  // which is also default-off, so a fresh session never auto-speaks.
  useEffect(() => {
    setAutoplayState(readAutoplayPref(userId));
  }, [userId]);

  const setAutoplay = useCallback(
    (next: boolean) => {
      // Turning auto-play OFF should also silence whatever is playing now.
      if (!next) tts.stop();
      setAutoplayState(next);
      // Persist per user so an opt-in (or opt-out) sticks across sessions.
      writeAutoplayPref(userId, next);
    },
    [tts, userId],
  );

  const patchTurn = useCallback(
    (id: string, patch: (t: CopilotTurn) => Partial<CopilotTurn>) => {
      setTurns((prev) =>
        prev.map((t) => (t.id === id ? { ...t, ...patch(t) } : t)),
      );
    },
    [],
  );

  const remember = useCallback(
    (id: string | null) => {
      if (rememberActive) writeActiveConversationId(userId, id);
    },
    [rememberActive, userId],
  );

  // Create the conversation lazily on first use and remember its id (mirrored
  // into conversationIdRef for the async send() closure). Returns whether a
  // NEW thread was created so callers can refresh the history list.
  const ensureConversation = useCallback(async (): Promise<{
    id: string;
    createdNew: boolean;
  }> => {
    if (conversationIdRef.current) {
      return { id: conversationIdRef.current, createdNew: false };
    }
    if (!createOnDemand) {
      throw new Error('No conversation is open on this thread yet.');
    }
    const convo = await createConversation({
      model: selectedModelRef.current || undefined,
    });
    conversationIdRef.current = convo.id;
    setActiveId(convo.id);
    remember(convo.id);
    return { id: convo.id, createdNew: true };
  }, [createOnDemand, remember]);

  // Resume a saved thread: point the send() closure at it immediately (so a
  // send mid-load targets the right thread, not a new one), then fetch +
  // render its transcript. Mirrors the live render via detailToCopilotTurns.
  const loadConversation = useCallback(
    async (id: string) => {
      tts.stop();
      setSpeakingId(null);
      setError(null);
      setLoadFailed(false);
      conversationIdRef.current = id;
      setActiveId(id);
      remember(id);
      setLoadingConvo(true);
      try {
        const detail = await getConversationDetail(id);
        setTurns(detailToCopilotTurns(detail));
        // Restore the model picker from the thread (persists like the profile).
        setSelectedModelId(detail.conversation.model);
      } catch {
        setLoadFailed(true);
        setError('Could not load that conversation. It may have been removed.');
      } finally {
        setLoadingConvo(false);
      }
    },
    [tts, remember],
  );

  const resetConversation = useCallback(() => {
    tts.stop();
    setSpeakingId(null);
    conversationIdRef.current = null;
    setActiveId(null);
    remember(null);
    setTurns([]);
    setError(null);
    setLoadFailed(false);
  }, [tts, remember]);

  const withBusy = useCallback(async (work: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await work();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, []);

  const send = useCallback(
    async (raw: string, viaVoice: boolean) => {
      // A photo/file dropped with no words still sends — the backend hands
      // the file to Merlin (Read tool) and the recorder asks its follow-ups.
      const prompt =
        raw.trim() ||
        (pendingFilesRef.current.length > 0 ? '(see attached file(s))' : '');
      if (!prompt || busyRef.current) return;
      busyRef.current = true;
      setBusy(true);
      setError(null);

      const userTurnId = nextId();
      const asstId = nextId();
      // Snapshot + clear files attached for this turn (they upload first).
      const files = pendingFilesRef.current;
      const fileNames = files.map((p) => p.name);
      if (files.length > 0) attach.clear();
      const sentAt = new Date().toISOString();
      setTurns((prev) => [
        ...prev,
        {
          id: userTurnId,
          role: 'user',
          text: prompt,
          viaVoice,
          attachments: fileNames.length ? fileNames : undefined,
          createdAt: sentAt,
        },
        {
          id: asstId,
          role: 'assistant',
          spoken: null,
          blocks: [],
          usage: null,
          streaming: true,
          error: null,
          createdAt: sentAt,
        },
      ]);

      let createdNew = false;
      try {
        const ensured = await ensureConversation();
        const convId = ensured.id;
        createdNew = ensured.createdNew;
        // Upload any attached files BEFORE the turn so the backend has their
        // gs_uri, can tell the recorder their ids, and binds them to the
        // signal(s) afterward. A single failed upload shouldn't block the turn.
        for (const file of files) {
          try {
            await uploadAttachment(convId, file);
          } catch {
            /* non-fatal — the turn still runs */
          }
        }
        // Play the spoken rundown the MOMENT its voice_reply frame streams in
        // — once per turn, respecting the auto-play toggle — so it speaks
        // while the agent is still writing the report (see makeRundownPlayer).
        const playRundown = makeRundownPlayer({
          autoplay: () => autoplayRef.current,
          onPlay: (spoken) => {
            tts.stop();
            tts.speak(spoken);
            setSpeakingId(asstId);
          },
          onStop: () => {
            tts.stop();
            setSpeakingId(null);
          },
        });
        await streamCopilotMessage(
          convId,
          prompt,
          (evt) => {
            playRundown(evt);
            // Remember this turn's id so a Stop click targets exactly this run.
            if (evt.type === 'started') runIdRef.current = evt.run_id;
            // A user Stop — also silence a rundown that already started.
            if (evt.type === 'cancelled') {
              tts.stop();
              setSpeakingId(null);
            }
            // Fold every frame into the turn: blocks (thinking / tool /
            // answer), the spoken rundown + brief, usage, and stream state.
            patchTurn(asstId, (t) => reduceCopilotEvent(t, evt));
          },
          undefined,
          selectedModelRef.current || undefined,
        );
      } catch (e) {
        patchTurn(asstId, () => ({
          error: e instanceof Error ? e.message : 'Something went wrong.',
          streaming: false,
        }));
      } finally {
        patchTurn(asstId, () => ({ streaming: false }));
        busyRef.current = false;
        setBusy(false);
        setCancelling(false);
        runIdRef.current = null;
        // A brand-new thread now exists server-side (with an auto-generated
        // title once the turn settled) — let the host refresh its history.
        if (createdNew) onCreatedRef.current?.();
      }
    },
    [attach, ensureConversation, nextId, patchTurn, tts],
  );

  const handleAudio = useCallback(
    async (result: AudioRecordingResult) => {
      setTranscribing(true);
      setError(null);
      let text = '';
      try {
        const { transcript } = await transcribeAudio(result.blob, result.mimeType);
        text = transcript.trim();
      } catch {
        setError('Could not transcribe audio. Please try again.');
        return;
      } finally {
        // Clear "Transcribing…" the moment transcription resolves — NOT after
        // the whole turn. send() drives its own `busy` ("Working…") state.
        setTranscribing(false);
      }
      if (!text) {
        setError("I didn't catch that — please try again.");
        return;
      }
      await send(text, true);
    },
    [send],
  );

  const replay = useCallback(
    (turnId: string, spoken: string) => {
      setSpeakingId(turnId);
      tts.stop();
      tts.speak(spoken);
    },
    [tts],
  );

  const stopSpeaking = useCallback(() => {
    tts.stop();
    setSpeakingId(null);
  }, [tts]);

  const sendText = useCallback((text: string) => void send(text, false), [send]);

  const submitTyped = useCallback(() => {
    const v = textValue;
    setTextValue('');
    void send(v, false);
  }, [textValue, send]);

  // Hard-stop the in-flight turn. The server tears the turn down, keeps the
  // partial reply in the thread, and emits the terminal cancelled/done
  // frames on the existing stream — those settle the UI (send()'s finally
  // re-enables the composer), so this handler only fires the request.
  const handleStop = useCallback(async () => {
    const convId = conversationIdRef.current;
    if (!convId || !busyRef.current || cancelling) return;
    setCancelling(true);
    try {
      await cancelMessage(convId, runIdRef.current);
    } catch {
      /* the turn may have just finished — the stream's frames settle the UI */
    }
  }, [cancelling]);

  return {
    turns,
    setTurns,
    patchTurn,
    nextId,
    busy,
    cancelling,
    transcribing,
    loadingConvo,
    loadFailed,
    error,
    setError,
    activeId,
    loadConversation,
    resetConversation,
    ensureConversation,
    withBusy,
    send,
    handleAudio,
    handleStop,
    textValue,
    setTextValue,
    submitTyped,
    sendText,
    attach,
    replay,
    stopSpeaking,
    speakingId,
    isSpeaking: tts.isSpeaking,
    isSynthesizing: tts.isSynthesizing,
    autoplay,
    setAutoplay,
    voice,
    setVoice,
    speed,
    setSpeed,
    models,
    defaultModel,
    selectedModelId,
    setSelectedModelId,
    strongestModel,
    totals,
  };
}

/** The per-user remembered thread id (the Ask tab's resume target). */
export function rememberedConversationId(userId: string | null): string | null {
  return readActiveConversationId(userId);
}
